import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  Assessment,
  AttachMoney,
  Schedule,
  Business,
  LocationOn,
  Star
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface HRMetrics {
  total_employees: number;
  new_hires_this_month: number;
  terminations_this_month: number;
  turnover_rate: number;
  employee_by_type: Record<string, number>;
  employee_by_location: Record<string, number>;
  employee_by_department: Record<string, number>;
  avg_performance_rating: number;
  goals_completion_rate: number;
  reviews_completed: number;
  reviews_overdue: number;
  total_payroll_cost: number;
  avg_salary: number;
  payroll_cost_per_employee: number;
  avg_hours_per_employee: number;
  overtime_hours: number;
  absence_rate: number;
  headcount_trend: Array<{ month: string; count: number }>;
  turnover_trend: Array<{ month: string; rate: number }>;
  payroll_trend: Array<{ month: string; cost: number }>;
}

interface DepartmentAnalytics {
  department_name: string;
  total_employees: number;
  avg_performance_rating: number;
  total_payroll_cost: number;
  avg_salary: number;
  manager_name: string | null;
  span_of_control: number;
  goals_completion_rate: number;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const HRAnalyticsDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<HRMetrics | null>(null);
  const [departments, setDepartments] = useState<DepartmentAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    fetchHRMetrics();
    fetchDepartmentAnalytics();
  }, []);

  const fetchHRMetrics = async () => {
    try {
      // Mock data for now - replace with actual API call
      const mockMetrics: HRMetrics = {
        total_employees: 47,
        new_hires_this_month: 3,
        terminations_this_month: 1,
        turnover_rate: 8.5,
        employee_by_type: {
          'permanent': 35,
          'contract': 8,
          'temporary': 3,
          'apprentice': 1
        },
        employee_by_location: {
          'office': 28,
          'remote': 12,
          'hybrid': 5,
          'field': 2
        },
        employee_by_department: {
          'Sales': 12,
          'Operations': 15,
          'Finance': 6,
          'Marketing': 8,
          'HR': 3,
          'IT': 3
        },
        avg_performance_rating: 3.8,
        goals_completion_rate: 76.5,
        reviews_completed: 8,
        reviews_overdue: 3,
        total_payroll_cost: 156750.00,
        avg_salary: 3335.11,
        payroll_cost_per_employee: 3335.11,
        avg_hours_per_employee: 37.5,
        overtime_hours: 45.2,
        absence_rate: 2.1,
        headcount_trend: [
          { month: 'January', count: 42 },
          { month: 'February', count: 44 },
          { month: 'March', count: 45 },
          { month: 'April', count: 46 },
          { month: 'May', count: 47 },
          { month: 'June', count: 47 }
        ],
        turnover_trend: [
          { month: 'January', rate: 12.0 },
          { month: 'February', rate: 10.5 },
          { month: 'March', rate: 9.2 },
          { month: 'April', rate: 8.8 },
          { month: 'May', rate: 8.5 },
          { month: 'June', rate: 8.5 }
        ],
        payroll_trend: [
          { month: 'January', cost: 142000 },
          { month: 'February', cost: 148500 },
          { month: 'March', cost: 151200 },
          { month: 'April', cost: 154800 },
          { month: 'May', cost: 156750 },
          { month: 'June', cost: 156750 }
        ]
      };

      setMetrics(mockMetrics);
    } catch (err) {
      setError('Failed to fetch HR metrics');
    }
  };

  const fetchDepartmentAnalytics = async () => {
    try {
      // Mock data for now - replace with actual API call
      const mockDepartments: DepartmentAnalytics[] = [
        {
          department_name: 'Sales',
          total_employees: 12,
          avg_performance_rating: 4.1,
          total_payroll_cost: 48000,
          avg_salary: 4000,
          manager_name: 'Sarah Johnson',
          span_of_control: 11,
          goals_completion_rate: 82.5
        },
        {
          department_name: 'Operations',
          total_employees: 15,
          avg_performance_rating: 3.7,
          total_payroll_cost: 52500,
          avg_salary: 3500,
          manager_name: 'Mike Chen',
          span_of_control: 14,
          goals_completion_rate: 74.2
        },
        {
          department_name: 'Finance',
          total_employees: 6,
          avg_performance_rating: 4.0,
          total_payroll_cost: 27000,
          avg_salary: 4500,
          manager_name: 'Emma Wilson',
          span_of_control: 5,
          goals_completion_rate: 88.9
        },
        {
          department_name: 'Marketing',
          total_employees: 8,
          avg_performance_rating: 3.9,
          total_payroll_cost: 28000,
          avg_salary: 3500,
          manager_name: 'David Brown',
          span_of_control: 7,
          goals_completion_rate: 71.4
        }
      ];

      setDepartments(mockDepartments);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch department analytics');
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactElement;
    trend?: 'up' | 'down' | 'stable';
    color?: string;
  }> = ({ title, value, icon, trend, color = 'primary' }) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div">
              {value}
            </Typography>
          </Box>
          <Box sx={{ color: `${color}.main` }}>
            {icon}
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {trend === 'up' && <TrendingUp fontSize="small" color="success" />}
                {trend === 'down' && <TrendingDown fontSize="small" color="error" />}
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!metrics) {
    return <Alert severity="warning">No HR metrics available</Alert>;
  }

  const employeeTypeData = Object.entries(metrics.employee_by_type).map(([type, count]) => ({
    name: type.charAt(0).toUpperCase() + type.slice(1),
    value: count
  }));

  const employeeLocationData = Object.entries(metrics.employee_by_location).map(([location, count]) => ({
    name: location.charAt(0).toUpperCase() + location.slice(1),
    value: count
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        HR Analytics Dashboard
      </Typography>
      
      <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="Workforce" />
        <Tab label="Performance" />
        <Tab label="Departments" />
      </Tabs>

      {selectedTab === 0 && (
        <Grid container spacing={3}>
          {/* Key Metrics Cards */}
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Employees"
              value={metrics.total_employees}
              icon={<People fontSize="large" />}
              trend="up"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="New Hires (Month)"
              value={metrics.new_hires_this_month}
              icon={<TrendingUp fontSize="large" />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Turnover Rate"
              value={formatPercentage(metrics.turnover_rate)}
              icon={<TrendingDown fontSize="large" />}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Avg Performance"
              value={`${metrics.avg_performance_rating.toFixed(1)}/5`}
              icon={<Star fontSize="large" />}
              color="success"
            />
          </Grid>

          {/* Headcount Trend */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Headcount Trend (6 Months)
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={metrics.headcount_trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#8884d8" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Employee Distribution */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Employee Types
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={employeeTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {employeeTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {selectedTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Department Analytics
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Department</TableCell>
                        <TableCell align="right">Employees</TableCell>
                        <TableCell align="right">Manager</TableCell>
                        <TableCell align="right">Avg Performance</TableCell>
                        <TableCell align="right">Goals Complete</TableCell>
                        <TableCell align="right">Payroll Cost</TableCell>
                        <TableCell align="right">Avg Salary</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {departments.map((dept) => (
                        <TableRow key={dept.department_name}>
                          <TableCell component="th" scope="row">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Business sx={{ mr: 1, color: 'text.secondary' }} />
                              {dept.department_name}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{dept.total_employees}</TableCell>
                          <TableCell align="right">{dept.manager_name || 'N/A'}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                              <Star sx={{ mr: 0.5, fontSize: 16, color: 'gold' }} />
                              {dept.avg_performance_rating.toFixed(1)}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                              <LinearProgress
                                variant="determinate"
                                value={dept.goals_completion_rate}
                                sx={{ width: 60, mr: 1 }}
                              />
                              {formatPercentage(dept.goals_completion_rate)}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(dept.total_payroll_cost)}</TableCell>
                          <TableCell align="right">{formatCurrency(dept.avg_salary)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default HRAnalyticsDashboard;
